import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Loader2, MapPin, Users, UserPlus, Lock, Unlock, Clock, AlertTriangle,
  RefreshCw, Trophy, Target, Crown, Search, Pencil, Plus, Store, CalendarClock, Layers, Trash2,
  Wand2, ArrowRight, Sparkles, TrendingUp, ChevronDown,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { textColorFor, frenchDate } from "@/lib/shariti";
import { toast } from "sonner";

// --- Métadonnées de statut de zone (couleurs charte Shariti) ---
const ZONE_STATUS = {
  libre:         { label: "Libre",         icon: Unlock,        cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] border-[rgba(31,157,85,0.30)]" },
  occupee:       { label: "Occupée",       icon: Lock,          cls: "bg-[rgba(24,24,24,0.06)] text-[rgba(24,24,24,0.72)] border-[rgba(24,24,24,0.14)]" },
  bientot_libre: { label: "Bientôt libre", icon: Clock,         cls: "bg-[rgba(252,219,83,0.40)] text-[#5A4200] border-[rgba(252,219,83,0.65)]" },
  a_renouveler:  { label: "À renouveler",  icon: AlertTriangle, cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226] border-[rgba(214,69,69,0.30)]" },
  mutualisee:    { label: "Mutualisée",    icon: Users,         cls: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8] border-[rgba(47,111,237,0.28)]" },
};

function initials(name) {
  return (name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

export const ZonesManager = () => {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [edit, setEdit] = useState(undefined); // undefined=closed, null=create, obj=edit
  const [aiOpen, setAiOpen] = useState(false);

  const load = () => api.get("/admin/zones/overview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const cdps = data?.cdp_summary || [];

  const assign = async (zone, cdpId) => {
    setBusyId(zone.id);
    try {
      const { data: res } = await api.post(`/admin/zones/${zone.id}/assign`, { cdp_id: cdpId });
      toast.success(`Zone ${zone.code} attribuée à ${res.cdp_name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'attribution"); }
    finally { setBusyId(null); }
  };
  const release = async (zone) => {
    setBusyId(zone.id);
    try {
      await api.post(`/admin/zones/${zone.id}/release`);
      toast.success(`Zone ${zone.code} libérée`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };
  const rotate = async (zone) => {
    setBusyId(zone.id);
    try {
      await api.post(`/admin/zones/${zone.id}/rotate`);
      toast.success(`Cycle de rotation renouvelé pour ${zone.code}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };
  // Granularité par DÉPARTEMENT : affecte un département précis à un CDP (ou remet au titulaire si null).
  const assignDept = async (zone, dept, cdpId) => {
    setBusyId(zone.id);
    try {
      const { data: res } = await api.post(`/admin/zones/${zone.id}/assign-department`, { dept, cdp_id: cdpId || null });
      toast.success(res.cdp_id ? `Département ${dept} attribué` : `Département ${dept} remis au titulaire`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'attribution"); }
    finally { setBusyId(null); }
  };

  if (!data) return <ZonesSkeleton />;

  const k = data.kpis;

  return (
    <div className="space-y-5" data-testid="zones-manager">
      {/* --- Bandeau gamifié : couverture + compteurs --- */}
      <CoveragePanel k={k} />

      {/* --- Contenu principal : zones + récap CDP --- */}
      <div className="grid gap-5 @5xl/page:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-bold"><MapPin className="h-4 w-4 text-[var(--shariti-orange)]" />Zones ({data.zones.length})</h3>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setAiOpen(true)} data-testid="zone-ai-suggest"
                className="bg-[var(--shariti-orange)] text-white hover:bg-[#D9621F]">
                <Wand2 className="mr-1.5 h-4 w-4" />Suggestion IA
              </Button>
              <Button size="sm" variant="outline" className="border-[var(--border-soft)]" onClick={() => setEdit(null)} data-testid="zone-new">
                <Plus className="mr-1.5 h-4 w-4" />Nouvelle zone
              </Button>
            </div>
          </div>
          <div className="grid gap-3 @2xl/page:grid-cols-2">
            {data.zones.map((z, i) => (
              <ZoneCard
                key={z.id} zone={z} cdps={cdps} index={i}
                busy={busyId === z.id}
                onAssign={(cdpId) => assign(z, cdpId)}
                onAssignDept={(dept, cdpId) => assignDept(z, dept, cdpId)}
                onRelease={() => release(z)}
                onRotate={() => rotate(z)}
                onEdit={() => setEdit(z)}
              />
            ))}
          </div>
        </div>

        <CdpSummary cdps={cdps} />
      </div>

      <ZoneEditDialog
        zone={edit} cdps={cdps} open={edit !== undefined}
        onClose={() => setEdit(undefined)}
        onSaved={() => { setEdit(undefined); load(); }}
      />

      <AiSuggestionDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onApplied={() => { setAiOpen(false); load(); }}
      />
    </div>
  );
};

// --- Dialog Suggestion IA de réattribution ---
function AiSuggestionDialog({ open, onClose, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [sugg, setSugg] = useState(null);

  useEffect(() => {
    if (!open) { setSugg(null); return; }
    setLoading(true);
    api.get("/admin/zones/ai-suggestion")
      .then((r) => setSugg(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Erreur de suggestion IA"))
      .finally(() => setLoading(false));
  }, [open]);

  const changes = (sugg?.proposal || []).filter((p) => p.change);

  const apply = async () => {
    setApplying(true);
    try {
      const assignments = changes.map((p) => ({ zone_id: p.zone_id, cdp_id: p.suggested_cdp_id }));
      const { data } = await api.post("/admin/zones/bulk-assign", { assignments });
      toast.success(`${data.count} zone(s) réattribuée(s) selon la suggestion IA`);
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'application"); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="zone-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-[var(--shariti-orange)]" />Suggestion IA — réattribution des zones</DialogTitle>
          <DialogDescription>Répartition pondérée par la performance historique (rendement = dons validés par journée réalisée).</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-10 text-muted-ink" data-testid="zone-ai-loading">
            <Loader2 className="h-7 w-7 animate-spin text-[var(--shariti-orange)]" />
            <p className="text-sm">Analyse des performances en cours…</p>
          </div>
        ) : sugg ? (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            <div className="grid gap-2 @lg/page:grid-cols-2">
              {sugg.cdps.map((c) => {
                const delta = Math.round((c.projected_share_pct - c.current_share_pct) * 10) / 10;
                return (
                  <div key={c.id} className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`zone-ai-cdp-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: c.color }} />
                      <span className="text-sm font-bold">{c.name}</span>
                      <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-muted-ink"><TrendingUp className="h-3 w-3" />{c.avg_per_day} don/j · {c.journees} j</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="text-muted-ink">{c.current_share_pct}%</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-ink" />
                      <span className="text-lg font-extrabold text-[var(--shariti-ink)]">{c.projected_share_pct}%</span>
                      {delta !== 0 && <span className={`text-[11px] font-bold ${delta > 0 ? "text-[#0F5A2F]" : "text-[#9B2226]"}`}>{delta > 0 ? "+" : ""}{delta} pt</span>}
                      <span className="ml-auto text-[11px] text-muted-ink">{c.projected_spots} spots</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {c.projected_zone_codes.map((z) => <span key={z} className="rounded-full border border-[var(--border-soft)] bg-white px-1.5 py-0.5 text-[9px] font-bold">{z}</span>)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Changements proposés ({changes.length})</div>
              {changes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-4 text-center text-sm text-muted-ink" data-testid="zone-ai-nochange">
                  La répartition actuelle est déjà optimale — aucun changement recommandé.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {changes.map((p) => (
                    <div key={p.zone_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-3 py-2 text-[13px]" data-testid={`zone-ai-change-${p.code}`}>
                      <span className="font-bold">{p.name}</span>
                      <span className="text-[10px] text-muted-ink">{(p.departments || []).join(", ")} · {p.spot_count} spots</span>
                      <span className="ml-auto flex items-center gap-1.5">
                        <span className="text-muted-ink line-through">{p.current_cdp_name || "Libre"}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                        <span className="font-bold text-[var(--shariti-orange)]">{p.suggested_cdp_name}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {sugg.rationale && (
              <div className="rounded-xl border border-[rgba(238,114,46,0.25)] bg-[rgba(238,114,46,0.05)] p-3" data-testid="zone-ai-rationale">
                <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold text-[var(--shariti-orange)]">
                  <Sparkles className="h-3.5 w-3.5" />{sugg.ai_generated ? "Analyse de l'IA" : "Analyse"}
                </div>
                <p className="text-[13px] leading-relaxed text-ink">{sugg.rationale}</p>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="zone-ai-close">Fermer</Button>
          <Button onClick={apply} disabled={applying || loading || changes.length === 0}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#D9621F]" data-testid="zone-ai-apply">
            {applying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1.5 h-4 w-4" />}
            Appliquer{changes.length ? ` (${changes.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Bandeau couverture (barre de progression + compteurs) ---
function CoveragePanel({ k }) {
  const counters = [
    { key: "free", label: "Libres", value: k.free, icon: Unlock, color: "#1F9D55", testid: "zones-kpi-free" },
    { key: "soon", label: "Bientôt libres", value: k.soon_free, icon: Clock, color: "#B8860B", testid: "zones-kpi-soon" },
    { key: "renew", label: "À renouveler", value: k.to_renew, icon: AlertTriangle, color: "#D64545", testid: "zones-kpi-renew" },
    { key: "mut", label: "Mutualisées", value: k.mutualisees, icon: Users, color: "#2F6FED", testid: "zones-kpi-mut" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]"
      data-testid="zones-coverage"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-[rgba(238,114,46,0.12)]"><Target className="h-5 w-5 text-[var(--shariti-orange)]" /></span>
            <div>
              <h3 className="text-sm font-bold leading-tight">Couverture des zones</h3>
              <p className="text-xs text-muted-ink">Chaque zone a un seul CDP titulaire — zéro chevauchement.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-2)] px-3 py-1.5 text-xs font-semibold" data-testid="zones-kpi-cdp-active">
            <Users className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />{k.cdp_active}/{k.total_cdp} CDP actifs
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-2)] px-3 py-1.5 text-xs font-semibold" data-testid="zones-kpi-rotation">
            <CalendarClock className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Rotation {k.rotation_months} mois
          </span>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-end justify-between">
          <span className="text-xs font-semibold text-muted-ink">{k.assigned}/{k.total} zones attribuées</span>
          <span className="flex items-center gap-1 text-2xl font-extrabold leading-none text-[var(--shariti-ink)]" data-testid="zones-coverage-value">
            {k.coverage_pct}%
            {k.coverage_pct === 100 && <Trophy className="h-5 w-5 text-[#E9B800]" />}
          </span>
        </div>
        <Progress value={k.coverage_pct} className="h-2.5" data-testid="zones-coverage-bar" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2.5 @3xl/page:grid-cols-4">
        {counters.map((c) => (
          <div key={c.key} className="flex items-center gap-2.5 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2.5" data-testid={c.testid}>
            <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${c.color}1F` }}>
              <c.icon className="h-4 w-4" style={{ color: c.color }} />
            </span>
            <div className="min-w-0">
              <div className="text-lg font-extrabold leading-none">{c.value}</div>
              <div className="break-words text-[11px] text-muted-ink">{c.label}</div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// --- Carte d'une zone ---
function ZoneCard({ zone, cdps, index, busy, onAssign, onAssignDept, onRelease, onRotate, onEdit }) {
  const meta = ZONE_STATUS[zone.status] || ZONE_STATUS.occupee;
  const StatusIcon = meta.icon;
  const color = zone.cdp_color || "#F89F63";
  const isAssigned = !!zone.cdp_id;
  const [showDepts, setShowDepts] = useState(false);
  // La granularité par département n'a de sens que pour une zone NON mutualisée à ≥ 2 départements.
  const canSplit = !zone.is_mutualise && (zone.departments || []).length >= 2;

  // Barre de cycle de rotation
  const cycleDays = Math.max(1, (zone.rotation_months || 3) * 30);
  const remaining = zone.days_remaining;
  const pct = remaining == null ? 0 : Math.max(0, Math.min(100, Math.round(((cycleDays - remaining) / cycleDays) * 100)));
  const rotationLabel = remaining == null
    ? "Rotation non démarrée"
    : remaining < 0 ? `Cycle dépassé de ${Math.abs(remaining)} j` : `Libre dans ${remaining} j`;

  const accent = isAssigned
    ? `color-mix(in srgb, #ffffff 82%, ${color} 18%)`
    : "rgba(31,157,85,0.06)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.03, 0.3) }}
      whileHover={{ y: -2 }}
      className="group flex flex-col rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]"
      style={{ borderTop: `3px solid ${isAssigned ? color : "rgba(31,157,85,0.5)"}` }}
      data-testid={`zone-card-${zone.code}`}
    >
      {/* En-tête */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="break-words text-sm font-bold">{zone.name}</span>
            <Badge variant="secondary" className="bg-[var(--surface-2)] text-[10px] font-mono">{zone.code}</Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-ink">
            <MapPin className="h-3 w-3" />{zone.departments?.length ? zone.departments.join(", ") : "Aucun département"}
          </div>
        </div>
        <Badge className={`shrink-0 gap-1 border ${meta.cls}`} data-testid={`zone-status-${zone.code}`}>
          <StatusIcon className="h-3 w-3" />{meta.label}
        </Badge>
      </div>

      {/* Titulaire */}
      <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: accent }}>
        {isAssigned ? (
          <>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-bold" style={{ background: color, color: textColorFor(color) }}>
              {initials(zone.cdp_name)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-[13px] font-bold" data-testid={`zone-cdp-name-${zone.code}`}>
                <Crown className="h-3 w-3 text-[#B8860B]" />{zone.cdp_name}
              </div>
              <div className="flex items-center gap-1 text-[11px] text-muted-ink">
                <Store className="h-3 w-3" />{zone.spot_count} spot{zone.spot_count > 1 ? "s" : ""}
              </div>
            </div>
          </>
        ) : (
          <div className="flex w-full items-center gap-2 text-[13px] font-semibold text-[#0F5A2F]">
            <Unlock className="h-4 w-4" />Zone disponible
            <span className="ml-auto flex items-center gap-1 text-[11px] font-medium text-muted-ink"><Store className="h-3 w-3" />{zone.spot_count} spot{zone.spot_count > 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      {/* Co-titulaires (zone mutualisée) */}
      {zone.is_mutualise && zone.shared_cdps?.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1" data-testid={`zone-shared-${zone.code}`}>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-ink">Co-titulaires :</span>
          {zone.shared_cdps.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-semibold">
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}
            </span>
          ))}
        </div>
      )}

      {/* Rotation */}
      {isAssigned && (
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1 text-muted-ink"><CalendarClock className="h-3 w-3" />{rotationLabel}</span>
            {zone.assigned_at && <span className="text-muted-ink">depuis le {frenchDate(zone.assigned_at)}</span>}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
            <div
              className="h-full rounded-full transition-[width] duration-300"
              style={{ width: `${pct}%`, background: zone.status === "a_renouveler" ? "#D64545" : zone.status === "bientot_libre" ? "#E9B800" : color }}
            />
          </div>
        </div>
      )}

      {/* Répartition par DÉPARTEMENT (granularité intra-zone) */}
      {canSplit && (
        <div className="mt-3">
          <button
            type="button" onClick={() => setShowDepts((v) => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-2.5 py-1.5 text-[11px] font-semibold text-muted-ink transition-colors hover:bg-white"
            data-testid={`zone-depts-toggle-${zone.code}`}
          >
            <span className="flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
              Répartition par département
              {zone.is_split && <span className="rounded-full bg-[rgba(238,114,46,0.12)] px-1.5 py-0.5 text-[9px] font-bold text-[#8A3A10]">personnalisée</span>}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDepts ? "rotate-180" : ""}`} />
          </button>
          {showDepts && (
            <div className="mt-1.5 space-y-1" data-testid={`zone-depts-${zone.code}`}>
              {(zone.department_owners || []).map((o) => (
                <div key={o.dept} className="flex items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-2 py-1.5" data-testid={`zone-dept-row-${zone.code}-${o.dept}`}>
                  <span className="shrink-0 rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px] font-mono font-bold">{o.dept}</span>
                  <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[12px]" data-testid={`zone-dept-owner-${zone.code}-${o.dept}`}>
                    {o.cdp_id ? (
                      <>
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10" style={{ background: o.cdp_color || "#ccc" }} />
                        <span className="truncate font-semibold">{o.cdp_name}</span>
                        {o.is_override
                          ? <span className="shrink-0 rounded-full border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.12)] px-1.5 py-0.5 text-[9px] font-bold text-[#8A3A10]">spécifique</span>
                          : <span className="shrink-0 text-[9px] text-muted-ink">titulaire</span>}
                      </>
                    ) : (
                      <span className="flex items-center gap-1 font-semibold text-[#0F5A2F]"><Unlock className="h-3 w-3" />Libre</span>
                    )}
                  </span>
                  <DeptAssignPopover zone={zone} dept={o.dept} cdps={cdps} busy={busy} onAssign={(cdpId) => onAssignDept(o.dept, cdpId)} />
                </div>
              ))}
              <p className="px-1 text-[10px] leading-snug text-muted-ink">
                Affecter un département à un autre CDP le retire du titulaire : chaque CDP ne verra que SES départements (prospection &amp; planning).
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex items-center gap-1.5 border-t border-[var(--border-soft)] pt-3">
        <AssignPopover zone={zone} cdps={cdps} busy={busy} onAssign={onAssign} />
        {isAssigned && (
          <Button size="sm" variant="ghost" className="h-8 gap-1 px-2 text-[12px]" disabled={busy} onClick={onRotate} data-testid={`zone-renew-${zone.code}`}>
            <RefreshCw className="h-3.5 w-3.5" />Renouveler
          </Button>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} title="Paramètres de la zone" data-testid={`zone-edit-${zone.code}`}>
            <Pencil className="h-4 w-4" />
          </Button>
          {isAssigned && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-[#D64545]" title="Libérer la zone" disabled={busy} data-testid={`zone-release-${zone.code}`}>
                  <Unlock className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Libérer la zone « {zone.name} » ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {zone.cdp_name} ne sera plus titulaire de cette zone. Les spots et l'historique sont conservés. La zone redeviendra attribuable.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid={`zone-release-cancel-${zone.code}`}>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={onRelease} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid={`zone-release-confirm-${zone.code}`}>Libérer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// --- Popover d'attribution (picker CDP) ---
function AssignPopover({ zone, cdps, busy, onAssign }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const isAssigned = !!zone.cdp_id;
  const filtered = cdps.filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className={isAssigned
            ? "h-8 gap-1 border border-[var(--border-soft)] bg-white px-2.5 text-[12px] text-[var(--shariti-ink)] hover:bg-[var(--surface-2)]"
            : "h-8 gap-1 bg-[var(--shariti-orange)] px-2.5 text-[12px] text-white hover:bg-[#E56622]"}
          disabled={busy}
          data-testid={`zone-assign-${zone.code}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isAssigned ? <><RefreshCw className="h-3.5 w-3.5" />Réattribuer</> : <><UserPlus className="h-3.5 w-3.5" />Attribuer</>)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Rechercher un CDP…" value={q} onValueChange={setQ} data-testid={`zone-assign-search-${zone.code}`} />
          <CommandList>
            <CommandEmpty>Aucun CDP.</CommandEmpty>
            <CommandGroup heading="Attribuer la zone à">
              {filtered.map((c) => (
                <CommandItem
                  key={c.id} value={c.id}
                  onSelect={() => { setOpen(false); onAssign(c.id); }}
                  disabled={c.id === zone.cdp_id}
                  data-testid={`zone-assign-opt-${zone.code}-${c.id}`}
                >
                  <span className="mr-2 h-3 w-3 rounded-full border border-black/10" style={{ background: c.color }} />
                  <span className="flex-1 break-words text-xs font-semibold">{c.name}</span>
                  <span className="text-[10px] text-muted-ink">{c.zone_count} zone{c.zone_count > 1 ? "s" : ""}</span>
                  {c.id === zone.cdp_id && <Crown className="ml-2 h-3 w-3 text-[#B8860B]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// --- Popover d'attribution PAR DÉPARTEMENT (picker CDP + remise au titulaire) ---
function DeptAssignPopover({ zone, dept, cdps, busy, onAssign }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = cdps.filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));
  const current = (zone.department_owners || []).find((o) => o.dept === dept);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm" variant="ghost"
          className="h-7 shrink-0 gap-1 px-2 text-[11px] font-semibold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)]"
          disabled={busy} data-testid={`zone-dept-assign-${zone.code}-${dept}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><UserPlus className="h-3.5 w-3.5" />Affecter</>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Rechercher un CDP…" value={q} onValueChange={setQ} data-testid={`zone-dept-search-${zone.code}-${dept}`} />
          <CommandList>
            <CommandEmpty>Aucun CDP.</CommandEmpty>
            <CommandGroup heading={`Département ${dept} → CDP`}>
              {current?.is_override && (
                <CommandItem value="__reset__" onSelect={() => { setOpen(false); onAssign(null); }} data-testid={`zone-dept-reset-${zone.code}-${dept}`}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5 text-muted-ink" />
                  <span className="text-xs font-semibold">Remettre au titulaire{zone.cdp_name ? ` (${zone.cdp_name})` : ""}</span>
                </CommandItem>
              )}
              {filtered.map((c) => (
                <CommandItem
                  key={c.id} value={c.id}
                  onSelect={() => { setOpen(false); onAssign(c.id); }}
                  disabled={c.id === current?.cdp_id}
                  data-testid={`zone-dept-opt-${zone.code}-${dept}-${c.id}`}
                >
                  <span className="mr-2 h-3 w-3 rounded-full border border-black/10" style={{ background: c.color }} />
                  <span className="flex-1 truncate text-xs font-semibold">{c.name}</span>
                  {c.id === current?.cdp_id && <Crown className="ml-2 h-3 w-3 text-[#B8860B]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// --- Récapitulatif par CDP (classement) ---
function CdpSummary({ cdps }) {
  const ranked = cdps.filter((c) => c.zone_count > 0);
  const idle = cdps.filter((c) => c.zone_count === 0);
  const maxSpots = Math.max(1, ...ranked.map((c) => c.spot_count));
  return (
    <div className="space-y-3" data-testid="zones-cdp-summary">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold"><Trophy className="h-4 w-4 text-[#E9B800]" />Classement CDP</h3>
        <div className="space-y-2.5">
          {ranked.length === 0 && <p className="text-xs text-muted-ink">Aucune zone attribuée pour l'instant.</p>}
          {ranked.map((c, i) => (
            <div key={c.id} className="flex items-center gap-2.5" data-testid={`zone-cdp-${c.id}`}>
              <span className="w-4 text-center text-xs font-bold text-muted-ink">{i + 1}</span>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-bold" style={{ background: c.color, color: textColorFor(c.color) }}>
                {initials(c.name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="break-words text-[13px] font-semibold">{c.name}</span>
                  <span className="shrink-0 text-[11px] font-bold text-muted-ink">{c.zone_count} · {c.spot_count} spots</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
                  <div className="h-full rounded-full" style={{ width: `${Math.round((c.spot_count / maxSpots) * 100)}%`, background: c.color }} />
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {c.zone_codes.map((z) => (
                    <span key={z} className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[9px] font-mono font-semibold text-muted-ink">{z}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {idle.length > 0 && (
        <div className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-4">
          <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold text-muted-ink"><Layers className="h-3.5 w-3.5" />CDP sans zone ({idle.length})</h4>
          <div className="flex flex-wrap gap-1.5">
            {idle.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[11px] font-semibold" data-testid={`zone-cdp-idle-${c.id}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Dialog édition / création d'une zone ---
const NONE = "__none__";
function ZoneEditDialog({ zone, cdps, open, onClose, onSaved }) {
  const isEdit = !!zone;
  const [form, setForm] = useState({ name: "", code: "", departments: "", rotation_months: 3, is_mutualise: false, shared_cdp_ids: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(zone
      ? { name: zone.name || "", code: zone.code || "", departments: (zone.departments || []).join(", "),
          rotation_months: zone.rotation_months || 3, is_mutualise: !!zone.is_mutualise,
          shared_cdp_ids: (zone.shared_cdps || []).map((c) => c.id) }
      : { name: "", code: "", departments: "", rotation_months: 3, is_mutualise: false, shared_cdp_ids: [] });
  }, [open, zone]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleShared = (id) => setForm((f) => ({
    ...f, shared_cdp_ids: f.shared_cdp_ids.includes(id) ? f.shared_cdp_ids.filter((x) => x !== id) : [...f.shared_cdp_ids, id],
  }));

  const submit = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error("Nom et code requis"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), code: form.code.trim().toUpperCase(),
        departments: form.departments.split(",").map((d) => d.trim()).filter(Boolean),
        rotation_months: Number(form.rotation_months) || 3,
        is_mutualise: form.is_mutualise,
        shared_cdp_ids: form.is_mutualise ? form.shared_cdp_ids : [],
      };
      if (isEdit) await api.put(`/admin/zones/${zone.id}`, payload);
      else await api.post("/admin/zones", payload);
      toast.success(isEdit ? "Zone mise à jour" : "Zone créée");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/zones/${zone.id}`);
      toast.success(`Zone ${zone.code} supprimée`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setDeleting(false); }
  };

  const eligibleShared = cdps.filter((c) => c.id !== zone?.cdp_id);
  const canDelete = isEdit && (zone?.spot_count || 0) === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="zone-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{isEdit ? `Paramètres — ${zone.name}` : "Nouvelle zone"}</DialogTitle>
          <DialogDescription>Nom, périmètre géographique et règle de rotation. Un CDP titulaire par zone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div><Label className="text-[11px]">Nom *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 h-9" data-testid="zone-form-name" /></div>
            <div><Label className="text-[11px]">Code *</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} className="mt-1 h-9 font-mono" placeholder="IDF-N" data-testid="zone-form-code" /></div>
          </div>
          <div><Label className="text-[11px]">Départements (séparés par des virgules)</Label><Input value={form.departments} onChange={(e) => set("departments", e.target.value)} placeholder="75, 92, 93" className="mt-1 h-9" data-testid="zone-form-depts" /></div>
          <div><Label className="text-[11px]">Rotation (mois)</Label><Input type="number" min="1" max="24" value={form.rotation_months} onChange={(e) => set("rotation_months", e.target.value)} className="mt-1 h-9" data-testid="zone-form-rotation" /></div>
          <div className="flex items-center justify-between rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2.5">
            <div><Label className="text-xs font-semibold">Zone mutualisée</Label><p className="text-[11px] text-muted-ink">Autorise plusieurs CDP co-titulaires.</p></div>
            <Switch checked={form.is_mutualise} onCheckedChange={(v) => set("is_mutualise", v)} data-testid="zone-form-mutualise" />
          </div>
          {form.is_mutualise && (
            <div>
              <Label className="text-[11px]">Co-titulaires</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="zone-form-shared">
                {eligibleShared.map((c) => {
                  const on = form.shared_cdp_ids.includes(c.id);
                  return (
                    <button
                      key={c.id} type="button" onClick={() => toggleShared(c.id)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${on ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[#8A3A10]" : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}
                      data-testid={`zone-form-shared-${c.id}`}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          {isEdit ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-[#D64545] hover:bg-[rgba(214,69,69,0.08)]" disabled={!canDelete || deleting} title={canDelete ? "Supprimer la zone" : "Zone avec spots : suppression impossible"} data-testid="zone-form-delete">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" />Supprimer</>}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer la zone « {zone.name} » ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {canDelete
                      ? "Cette zone n'a aucun spot rattaché. La suppression est définitive."
                      : `Cette zone comporte ${zone?.spot_count} spot(s) : réattribuez-les avant de pouvoir la supprimer.`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="zone-form-delete-cancel">Annuler</AlertDialogCancel>
                  <AlertDialogAction disabled={!canDelete} onClick={remove} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid="zone-form-delete-confirm">Supprimer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="zone-form-cancel">Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="zone-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ZonesSkeleton() {
  return (
    <div className="space-y-5" data-testid="zones-skeleton">
      <Skeleton className="h-40 w-full rounded-2xl" />
      <div className="grid gap-3 @2xl/page:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-52 w-full rounded-2xl" />)}
      </div>
    </div>
  );
}
